/**
 * TaskCard.tsx
 *
 * Universal Card component for TaskFlow items (Goal, Plan, Project, Task, Action).
 * 100% Theme-tokenized with fluid typography, progress indicator, inline notes toggle,
 * full keyboard accessibility (Enter/Space to open), native HTML5 drag-and-drop,
 * and internationalized labels via useTranslation() with <bdi> text isolation.
 */

import React, { useState } from 'react';
import type { TaskFlowItem, TaskFlowItemType } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

export interface TaskCardProps {
  item: TaskFlowItem;
  onClick?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  isSelected?: boolean;
}

const TYPE_ICONS: Record<TaskFlowItemType, { icon: string; key: string }> = {
  goal: { icon: '🎯', key: 'goal' },
  plan: { icon: '🗺️', key: 'plan' },
  project: { icon: '📦', key: 'project' },
  task: { icon: '✅', key: 'task' },
  action: { icon: '⚡', key: 'action' },
};

const PRIORITY_TOKENS: Record<string, { bg: string; text: string }> = {
  urgent: { bg: 'var(--theme-status-danger-bg)', text: 'var(--theme-status-danger-text)' },
  high: { bg: 'var(--theme-status-warning-bg)', text: 'var(--theme-status-warning-text)' },
  medium: { bg: 'var(--theme-accent-subtle)', text: 'var(--theme-accent)' },
  normal: { bg: 'var(--theme-bg-subtle)', text: 'var(--theme-text-secondary)' },
  low: { bg: 'var(--theme-bg-subtle)', text: 'var(--theme-text-muted)' },
};

export const TaskCard: React.FC<TaskCardProps> = ({
  item,
  onClick,
  onDragStart,
  onDragEnd,
  isSelected = false,
}) => {
  const { t, formatDate } = useTranslation();
  const [isInlineExpanded, setIsInlineExpanded] = useState(false);
  const typeMeta = TYPE_ICONS[item.type] || { icon: '📌', key: item.type };
  const priorityStyle = PRIORITY_TOKENS[item.priority || 'normal'] || PRIORITY_TOKENS.normal;

  // Due date / Overdue calculation
  let dueText: string | null = null;
  let isOverdue = false;
  if (item.dueDate) {
    const dueTime = new Date(item.dueDate).getTime();
    if (!isNaN(dueTime)) {
      const diffDays = Math.ceil((dueTime - Date.now()) / (1000 * 60 * 60 * 24));
      if (item.status !== 'done' && item.status !== 'cancelled' && item.status !== 'archived') {
        if (diffDays < 0) {
          dueText = `${Math.abs(diffDays)}d overdue`;
          isOverdue = true;
        } else if (diffDays === 0) {
          dueText = 'Due today';
        } else if (diffDays === 1) {
          dueText = 'Due tomorrow';
        } else {
          dueText = `Due in ${diffDays}d`;
        }
      } else {
        dueText = formatDate(item.dueDate);
      }
    }
  }

  const dependencyCount = item.relationships?.filter((r) => r.type === 'blockedBy' || r.type === 'dependsOn').length || 0;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  const localizedTypeName = t(`taskflow.types.${typeMeta.key}`) || typeMeta.key;
  const localizedPriority = t(`taskflow.priorities.${item.priority || 'normal'}`) || (item.priority || 'normal');

  return (
    <div
      tabIndex={0}
      role="button"
      aria-label={`${item.title} (${localizedTypeName}, status: ${item.status})`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={{
        backgroundColor: isSelected ? 'var(--theme-accent-subtle)' : 'var(--theme-bg-card)',
        border: isSelected ? '1px solid var(--theme-accent)' : '1px solid var(--theme-border-subtle)',
        borderRadius: 'var(--theme-radius-md, 8px)',
        padding: '12px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        transition: 'transform var(--theme-transition-fast), box-shadow var(--theme-transition-fast), border-color var(--theme-transition-fast)',
        userSelect: 'none',
        boxShadow: isSelected ? '0 0 0 1px var(--theme-accent), var(--theme-shadow-md)' : 'var(--theme-shadow-sm)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--theme-shadow-md)';
        if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-border-highlight)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isSelected ? '0 0 0 1px var(--theme-accent), var(--theme-shadow-md)' : 'var(--theme-shadow-sm)';
        if (!isSelected) e.currentTarget.style.borderColor = 'var(--theme-border-subtle)';
      }}
    >
      {/* Top Meta Header: Type Badge, Inline Expand & Priority */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            style={{
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 700,
              color: 'var(--theme-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: 'var(--theme-bg-subtle)',
              padding: '2px 6px',
              borderRadius: 'var(--theme-radius-sm, 4px)',
            }}
          >
            <span>{typeMeta.icon}</span>
            <span style={{ textTransform: 'capitalize' }}>{localizedTypeName}</span>
          </span>

          {/* Inline expand toggle button */}
          {item.description && (
            <button
              aria-label={isInlineExpanded ? 'Collapse notes' : 'Expand notes'}
              title={isInlineExpanded ? 'Collapse Inline Notes' : 'Expand Inline Notes'}
              onClick={(e) => {
                e.stopPropagation();
                setIsInlineExpanded((prev) => !prev);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--theme-text-muted)',
                cursor: 'pointer',
                fontSize: 'var(--theme-text-xs)',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isInlineExpanded ? '▼' : '▶'}
            </button>
          )}
        </div>

        {item.priority && item.priority !== 'normal' && (
          <span
            style={{
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 700,
              textTransform: 'uppercase',
              padding: '2px 6px',
              borderRadius: 'var(--theme-radius-sm, 4px)',
              backgroundColor: priorityStyle.bg,
              color: priorityStyle.text,
            }}
          >
            {localizedPriority}
          </span>
        )}
      </div>

      {/* Task Title */}
      <div
        style={{
          fontSize: 'var(--theme-text-sm)',
          fontWeight: 600,
          color: 'var(--theme-text-primary)',
          lineHeight: 'var(--theme-leading-normal)',
        }}
      >
        <bdi>{item.title}</bdi>
      </div>

      {/* Inline Expanded Description */}
      {isInlineExpanded && item.description && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            fontSize: 'var(--theme-text-xs)',
            color: 'var(--theme-text-secondary)',
            backgroundColor: 'var(--theme-bg-subtle)',
            padding: '8px 10px',
            borderRadius: 'var(--theme-radius-sm, 4px)',
            lineHeight: 'var(--theme-leading-normal)',
            borderInlineStart: '2px solid var(--theme-accent)',
          }}
        >
          <bdi>{item.description}</bdi>
        </div>
      )}

      {/* Progress Bar (if > 0 or goal/project) */}
      {(item.progressPercentage !== undefined && item.progressPercentage > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
          <div
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              backgroundColor: 'var(--theme-bg-subtle)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.min(100, Math.max(0, item.progressPercentage))}%`,
                height: '100%',
                backgroundColor: item.progressPercentage === 100 ? 'var(--theme-status-success-text)' : 'var(--theme-accent)',
                transition: 'width var(--theme-transition-normal)',
              }}
            />
          </div>
          <span style={{ fontSize: 'var(--theme-text-xs)', color: 'var(--theme-text-muted)', fontWeight: 600 }}>
            {item.progressPercentage}%
          </span>
        </div>
      )}

      {/* Bottom Footer: Tags, Dependencies, Due Date */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          {/* Tags */}
          {item.tags?.slice(0, 2).map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 'var(--theme-text-xs)',
                color: 'var(--theme-accent)',
                backgroundColor: 'var(--theme-accent-subtle)',
                padding: '1px 5px',
                borderRadius: 'var(--theme-radius-sm, 4px)',
              }}
            >
              <bdi>#{tag}</bdi>
            </span>
          ))}

          {/* Dependency Indicator */}
          {dependencyCount > 0 && (
            <span
              style={{
                fontSize: 'var(--theme-text-xs)',
                color: 'var(--theme-text-secondary)',
                backgroundColor: 'var(--theme-bg-subtle)',
                padding: '1px 5px',
                borderRadius: 'var(--theme-radius-sm, 4px)',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
              }}
            >
              🔗 {dependencyCount}
            </span>
          )}
        </div>

        {/* Due Date Chip */}
        {dueText && (
          <span
            style={{
              fontSize: 'var(--theme-text-xs)',
              fontWeight: 600,
              color: isOverdue ? 'var(--theme-status-danger-text)' : 'var(--theme-text-muted)',
              backgroundColor: isOverdue ? 'var(--theme-status-danger-bg)' : 'transparent',
              padding: isOverdue ? '1px 5px' : '0',
              borderRadius: 'var(--theme-radius-sm, 4px)',
            }}
          >
            {isOverdue ? `⚠️ ${dueText}` : `📅 ${dueText}`}
          </span>
        )}
      </div>
    </div>
  );
};
